/**
 * May a store seed default rows into a workspace right now?
 *
 * The workflow and cycle-template stores write a starter set when a read finds
 * none. That read is only evidence about the workspace it ran against, and
 * only while that workspace is still the one in force, resolved from the
 * server for the person signed in. Seeding on anything less writes rows into
 * the wrong workspace (or into none): on 2026-11-02 a tab that changed account
 * without a reload kept the previous person's context while every read went on.
 *
 * Dependency-free apart from the context modules, so a store can call it
 * without importing other stores.
 */

import { getCurrentWorkspaceId, isInDemoMode } from './supabaseSync';
import { getTenantContext } from './tenantContext';
import { getPageUserId, isAccountChangeInProgress } from './accountChange';
import { subscribeWorkspaceContext } from './workspaceContextEvents';

const armedRetries = new Set<string>();

/**
 * Read again the next time the workspace context changes. A read that found
 * the context in flux (or changed under it) is not lost: once the workspace
 * resolves, the store reads — and, if it is still empty, seeds — for it.
 * One retry per `key` at a time.
 */
export function retryWhenWorkspaceContextChanges(key: string, retry: () => void): void {
  if (armedRetries.has(key)) return;
  armedRetries.add(key);
  const off = subscribeWorkspaceContext(() => {
    off();
    armedRetries.delete(key);
    retry();
  });
}

/**
 * The workspace a workspace-scoped store reads and seeds for: the current one,
 * or null in demo mode (demo rows are written without a workspace).
 */
export function storeWorkspaceId(): string | null {
  return isInDemoMode() ? null : getCurrentWorkspaceId();
}

/**
 * Why defaults may not be seeded into `workspaceId` (the workspace the empty
 * read ran against) right now — or null when they may.
 */
export function workspaceSeedBlocker(workspaceId: string | null): string | null {
  if (isAccountChangeInProgress()) return 'the signed-in person is changing';
  if (isInDemoMode()) return null;
  if (!workspaceId) return 'no workspace resolved yet';
  if (getCurrentWorkspaceId() !== workspaceId) return 'the workspace changed during the read';
  const ctx = getTenantContext();
  // Without a context the id came from the sessionStorage hint, which can
  // still name the previous person's or the previous session's workspace.
  if (!ctx) return 'the workspace has not been resolved from the server yet';
  const pageUserId = getPageUserId();
  if (pageUserId && ctx.userId !== pageUserId) return 'the workspace context belongs to another person';
  return null;
}
